"use client";

/**
 * A shop's collections on its website (Petal_Some Ug., Sep 2026): cover
 * tiles that open /collections/<slug>. The strip sits above the products
 * ("Shop by collection"); the grid is the Collections tab.
 *
 * The caller passes collections already filtered to the visitor's day
 * (inSeason, lib/config/collections.ts) and branch. The branch rides along
 * in each link (?store=), so the page shows that branch's products.
 */

import Link from "next/link";
import { ChevronRight, Layers } from "lucide-react";

export interface ShopCollection {
  _id: string;
  name: string;
  slug: string;
  description: string | null;
  cover: string | null;
}

const hrefFor = (c: ShopCollection, storeId?: string) => `/collections/${c.slug}${storeId ? `?store=${encodeURIComponent(storeId)}` : ""}`;

function Cover({ c, className }: { c: ShopCollection; className: string }) {
  return c.cover ? (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={c.cover}
      alt={c.name}
      loading="lazy"
      className={`${className} object-cover transition-transform duration-500 group-hover:scale-105`}
    />
  ) : (
    <div className={`${className} flex items-center justify-center`} style={{ background: "linear-gradient(135deg, #0f172a 0%, #334155 100%)" }}>
      <Layers className="w-7 h-7 text-white/80" />
    </div>
  );
}

/** "Shop by collection" — a row of tiles above the products; scrolls sideways on a phone. */
export function CollectionStrip({ collections, storeId }: { collections: ShopCollection[]; storeId?: string }) {
  if (collections.length === 0) return null;
  return (
    <section aria-label="Shop by collection" className="mb-8">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-black uppercase tracking-widest" style={{ color: "#0f172a" }}>
          Shop by collection
        </h2>
      </div>
      <div className="-mx-6 px-6 scroll-px-6 flex gap-3 sm:gap-4 overflow-x-auto pb-2 snap-x" style={{ scrollbarWidth: "thin" }}>
        {collections.map((c) => (
          <Link key={c._id} href={hrefFor(c, storeId)} className="group snap-start shrink-0 w-32 sm:w-40">
            <div className="rounded-xl overflow-hidden aspect-[4/5]" style={{ backgroundColor: "#f1f5f9" }}>
              <Cover c={c} className="w-full h-full" />
            </div>
            <div className="mt-2 text-xs sm:text-sm font-bold leading-snug line-clamp-2" style={{ color: "#0f172a" }}>
              {c.name}
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

/** The Collections tab: every collection, larger, with its line about it. */
export function CollectionGrid({ collections, storeId }: { collections: ShopCollection[]; storeId?: string }) {
  return (
    <section id="collections" className="max-w-7xl mx-auto px-4 sm:px-6 pb-16">
      <div className="flex items-center gap-2.5 mb-6">
        <Layers className="w-5 h-5" style={{ color: "#0f172a" }} />
        <h2 className="text-xl sm:text-2xl font-black uppercase tracking-wide" style={{ color: "#0f172a" }}>
          Collections
        </h2>
        <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: "#f1f5f9", color: "#64748b" }}>
          {collections.length}
        </span>
      </div>
      {collections.length === 0 ? (
        <p className="text-sm py-10 text-center" style={{ color: "#64748b" }}>
          No collections right now.
        </p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
          {collections.map((c) => (
            <Link key={c._id} href={hrefFor(c, storeId)} className="group">
              <div className="rounded-2xl overflow-hidden aspect-[4/5]" style={{ backgroundColor: "#f1f5f9" }}>
                <Cover c={c} className="w-full h-full" />
              </div>
              <div className="mt-3 flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="text-sm sm:text-base font-bold leading-snug" style={{ color: "#0f172a" }}>
                    {c.name}
                  </h3>
                  {c.description ? (
                    <p className="mt-0.5 text-xs sm:text-sm line-clamp-2" style={{ color: "#64748b" }}>
                      {c.description}
                    </p>
                  ) : null}
                </div>
                <ChevronRight className="w-4 h-4 mt-1 shrink-0 transition-transform group-hover:translate-x-0.5" style={{ color: "#64748b" }} />
              </div>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}
